/**
 * Utility functions for converting legacy data to React Flow format
 */

/**
 * Helper to parse pixel values to numbers
 */
export function parsePixelValue(value: string): number {
  if (!value) return 0
  const num = parseFloat(value.replace('px', ''))
  return isNaN(num) ? 0 : num
}

/**
 * Convert legacy string boolean ("1" or "0") or number (0/1) to boolean
 * Per ADMIN_VS_LEGACY.md: Legacy uses numbers (0/1), Admin3 uses strings ("0"/"1")
 */
export function stringToBoolean(value: string | unknown): boolean {
  if (typeof value === 'string') {
    return value === '1' || value === 'true'
  }
  if (typeof value === 'number') {
    return value === 1
  }
  if (typeof value === 'boolean') {
    return value
  }
  return false
}

/**
 * Convert legacy string number to number
 */
export function stringToNumber(value: string | unknown): number | undefined {
  if (typeof value === 'string') {
    const num = parseInt(value, 10)
    return isNaN(num) ? undefined : num
  }
  if (typeof value === 'number') {
    return value
  }
  return undefined
}

/**
 * Get field value from legacy HTML object with fallback to camelCase
 */
export function getLegacyField(
  html: Record<string, unknown>,
  snakeCaseKey: string,
  camelCaseKey?: string,
): unknown {
  if (snakeCaseKey in html) {
    return html[snakeCaseKey]
  }
  if (camelCaseKey && camelCaseKey in html) {
    return html[camelCaseKey]
  }
  return undefined
}

/**
 * Get string field value from legacy HTML object
 * Converts string "null" and "undefined" to actual null/undefined
 */
export function getLegacyStringField(
  html: Record<string, unknown>,
  snakeCaseKey: string,
  camelCaseKey?: string,
): string | undefined {
  const value = getLegacyField(html, snakeCaseKey, camelCaseKey)
  if (value === null || value === undefined) {
    return undefined
  }
  const strValue = String(value)
  // Convert legacy string "null" and "undefined" to actual undefined
  if (strValue === 'null' || strValue === 'undefined') {
    return undefined
  }
  return strValue
}

/**
 * Get number field value from legacy HTML object
 */
export function getLegacyNumberField(
  html: Record<string, unknown>,
  snakeCaseKey: string,
  camelCaseKey?: string,
): number | undefined {
  const value = getLegacyField(html, snakeCaseKey, camelCaseKey)
  return stringToNumber(value)
}

/**
 * Get boolean field value from legacy HTML object
 */
export function getLegacyBooleanField(
  html: Record<string, unknown>,
  snakeCaseKey: string,
  camelCaseKey?: string,
): boolean | undefined {
  const value = getLegacyField(html, snakeCaseKey, camelCaseKey)
  if (value === undefined || value === null) {
    return undefined
  }
  return stringToBoolean(value)
}
